import React, { useState, useEffect, useCallback } from 'react'
import { Card, Form, Button, ListGroup, Badge, Row, Col, Container, InputGroup } from 'react-bootstrap'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../services/supabase'
import Layout from '../components/Layout'
import ProtectedRoute from '../components/ProtectedRoute'

function CheckinName() {
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const [participants, setParticipants] = useState([])
  const [filteredParticipants, setFilteredParticipants] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [session, setSession] = useState(null)
  const [presences, setPresences] = useState([])
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)

  // 🔒 Set des participant_id en cours de traitement (anti double-clic)
  const [processingIds, setProcessingIds] = useState(new Set())

  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      setUser(user)
    }
    getUser()
  }, [])

  const fetchSession = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('sessions')
        .select(`*, course_types (name, location)`)
        .eq('id', sessionId)
        .single()
      if (error) throw error
      setSession(data)
    } catch (error) {
      console.error('Error fetching session:', error)
    }
  }, [sessionId])

  const fetchParticipants = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('participants')
        .select('*')
        .order('last_name')
      if (error) throw error
      setParticipants(data || [])
      setFilteredParticipants(data || [])
    } catch (error) {
      console.error('Error fetching participants:', error)
    }
  }, [])

  const fetchPresences = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('presences')
        .select('*')
        .eq('session_id', sessionId)
      if (error) throw error
      setPresences(data || [])
    } catch (error) {
      console.error('Error fetching presences:', error)
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => {
    const loadData = async () => {
      await fetchSession()
      await fetchParticipants()
      await fetchPresences()
    }
    loadData()
  }, [fetchSession, fetchParticipants, fetchPresences])

  const handleSearch = (term) => {
    setSearchTerm(term)
    if (term.length === 0) {
      setFilteredParticipants(participants)
    } else {
      const filtered = participants.filter(p =>
        p.first_name.toLowerCase().includes(term.toLowerCase()) ||
        p.last_name.toLowerCase().includes(term.toLowerCase()) ||
        (p.email && p.email.toLowerCase().includes(term.toLowerCase()))
      )
      setFilteredParticipants(filtered)
    }
  }

  const markPresence = async (participantId) => {
    // 🔒 Anti double-clic : bloquer si déjà en cours
    if (processingIds.has(participantId)) return
    setProcessingIds(prev => new Set(prev).add(participantId))

    try {
      // ✅ upsert au lieu de insert — la contrainte UNIQUE en base garantit l'unicité
      const { error } = await supabase
        .from('presences')
        .upsert(
          [{
            participant_id: participantId,
            session_id: sessionId,
            mode: 'name',
          }],
          { onConflict: 'participant_id,session_id', ignoreDuplicates: true }
        )

      if (error) throw error
      await fetchPresences()
    } catch (error) {
      console.error('Error marking presence:', error)
      alert('Erreur lors du marquage de la présence: ' + error.message)
    } finally {
      setProcessingIds(prev => {
        const next = new Set(prev)
        next.delete(participantId)
        return next
      })
    }
  }

  const isPresent = (participantId) => presences.some(p => p.participant_id === participantId)

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout user={user}>
          <Container className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3">Chargement...</p>
          </Container>
        </Layout>
      </ProtectedRoute>
    )
  }

  if (!session) {
    return (
      <ProtectedRoute>
        <Layout user={user}>
          <Container className="text-center py-5">
            <div className="text-danger">
              <h4>Session non trouvée</h4>
              <p>La session demandée n'existe pas ou a été supprimée.</p>
              <Button variant="primary" onClick={() => navigate('/courses')}>
                Retour aux cours
              </Button>
            </div>
          </Container>
        </Layout>
      </ProtectedRoute>
    )
  }

  return (
    <ProtectedRoute>
      <Layout user={user}>
        <Container fluid="lg" className="py-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <div className="d-flex align-items-center mb-2">
                <Button
                  variant="outline-primary"
                  onClick={() => navigate('/courses')}
                  className="me-3"
                  size="lg"
                >
                  ◀ Retour
                </Button>
                <div>
                  <h2 className="h3 mb-1 fw-bold text-dark">Présence par Nom</h2>
                  <p className="text-muted mb-0">
                    📅 {new Date(session.date).toLocaleDateString('fr-FR', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    })}
                    <span className="mx-2">•</span>
                    📍 {session.course_types?.location || session.location || 'Lieu non spécifié'}
                  </p>
                  {session.course_types?.name && (
                    <p className="text-muted mb-0">🏷️ {session.course_types.name}</p>
                  )}
                </div>
              </div>
            </div>
            <Badge bg="light" text="dark" className="fs-6 px-3 py-2">
              👤 {presences.length} / {participants.length} présents
            </Badge>
          </div>

          <Row className="g-4">
            <Col lg={8}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-0 py-3">
                  <h5 className="mb-0 fw-semibold">🔍 Rechercher un participant</h5>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="p-4 border-bottom">
                    <InputGroup size="lg">
                      <InputGroup.Text className="bg-light border-end-0">🔍</InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Tapez le nom, prénom ou email..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="border-start-0"
                      />
                    </InputGroup>
                  </div>

                  <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {filteredParticipants.length === 0 ? (
                      <div className="text-center py-5">
                        <div style={{ fontSize: '3rem' }}>👥</div>
                        <h5 className="text-muted">Aucun participant trouvé</h5>
                        {searchTerm && (
                          <Button variant="outline-primary" onClick={() => handleSearch('')}>
                            🔄 Réinitialiser la recherche
                          </Button>
                        )}
                      </div>
                    ) : (
                      <ListGroup variant="flush">
                        {filteredParticipants.map(participant => {
                          const present = isPresent(participant.id)
                          const processing = processingIds.has(participant.id)
                          return (
                            <ListGroup.Item key={participant.id} className="px-4 py-3 border-bottom">
                              <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                  <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">👤</div>
                                  <div>
                                    <h6 className="mb-1 fw-semibold">
                                      {participant.first_name} {participant.last_name}
                                      {present && (
                                        <Badge bg="success" className="ms-2">✅ Présent</Badge>
                                      )}
                                    </h6>
                                    <p className="text-muted mb-0 small">
                                      {participant.email && <>📧 {participant.email}</>}
                                      {participant.email && participant.phone && ' • '}
                                      {participant.phone && <>📞 {participant.phone}</>}
                                    </p>
                                  </div>
                                </div>
                                <div>
                                  {present ? (
                                    <Badge bg="success" className="fs-6 px-3 py-2">✅ Présent</Badge>
                                  ) : (
                                    <Button
                                      variant="primary"
                                      onClick={() => markPresence(participant.id)}
                                      size="lg"
                                      className="px-4"
                                      disabled={processing}
                                    >
                                      {processing
                                        ? <><span className="spinner-border spinner-border-sm me-2" />En cours...</>
                                        : '✅ Marquer présent'
                                      }
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </ListGroup.Item>
                          )
                        })}
                      </ListGroup>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={4}>
              <Card className="shadow-sm border-0 sticky-top" style={{ top: '20px' }}>
                <Card.Header className="bg-primary text-white py-3">
                  <h5 className="mb-0 fw-semibold">📊 Statistiques</h5>
                </Card.Header>
                <Card.Body>
                  <div className="text-center mb-4">
                    <div className="display-4 fw-bold text-primary">{presences.length}</div>
                    <div className="text-muted">Participants présents</div>
                  </div>

                  <div className="row text-center mb-4">
                    <div className="col-6">
                      <div className="border-end">
                        <div className="h4 fw-bold text-success">{presences.filter(p => p.mode === 'qr').length}</div>
                        <div className="text-muted small">📷 Par QR</div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="h4 fw-bold text-info">{presences.filter(p => p.mode === 'name').length}</div>
                      <div className="text-muted small">👤 Manuel</div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Taux de présence</small>
                      <small>
                        {participants.length > 0
                          ? Math.round((presences.length / participants.length) * 100)
                          : 0}%
                      </small>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div
                        className="progress-bar bg-success"
                        style={{
                          width: participants.length > 0
                            ? `${(presences.length / participants.length) * 100}%`
                            : '0%'
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-top">
                    <div className="d-grid gap-2">
                      <Button variant="outline-primary" onClick={() => navigate(`/checkin/qr/${sessionId}`)} size="lg">
                        📷 Scanner QR Codes
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </Layout>
    </ProtectedRoute>
  )
}

export default CheckinName
